import {upperFirst} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {concatWithBrandName} from "../../../../app/utils/concat_with_brand_name";
import {Country} from "../../../../region/types/Country";
import {getFlatsRoomRangeSectionTexts} from "./common_texts/get_flats_room_range_section_texts";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const flatOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        flatsCountReady: props.region.stats?.properties_flats_count_ready || 0,
        flatsCountReadyInThreeMonths: props.region.stats?.properties_flats_count_ready_in_3m || 0,
        flatsCountReadyInSixMonths: props.region.stats?.properties_flats_count_ready_in_6m || 0,
        flatsCountReadyInOneYear: props.region.stats?.properties_flats_count_ready_in_12m || 0,
        flatsGroundFloor: props.region.stats?.properties_flats_ground_floor || 0,
        flatsGroundFloorWithGarden: props.region.stats?.properties_flats_ground_floor_with_garden || 0,
        flatsLowFloor: props.region.stats?.properties_flats_low_floor || 0,
        flatsHighFloor: props.region.stats?.properties_flats_high_floor || 0,
        flatsLastFloor: props.region.stats?.properties_flats_last_floor || 0,
        flatsLastFloorWithTerrace: props.region.stats?.properties_flats_last_floor_with_terrace || 0,
        flatsLatestConstructionDate: props.region.stats?.properties_flats_latest_construction_date || null,
        flatsOffersCountForSale: props.region.stats?.offers_flats_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0
    };
    const getGeneralPropertyCountText = (propertiesCount: number) => {
        return `${propertiesCount} ${pluralize(["lokal", "lokale", "lokali"])(propertiesCount)}`;
    };

    if (!variables.flatsOffersCountForSale || !variables.flatsPropertiesCountForSale) {
        return [];
    }

    if (props.region.country === Country.SPAIN) {
        return [
            {
                headline: `${variables.regionName} - mieszkanie w Hiszpanii`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: `W ${upperFirst(variables.regionName)} ${pluralize([
                            `dostępna jest ${numberFormat(variables.regionPropertiesCountForSale)} oferta mieszkania.`,
                            `dostępne są ${numberFormat(variables.regionPropertiesCountForSale)} oferty mieszkań.`,
                            `dostępnych jest ${numberFormat(variables.regionPropertiesCountForSale)} ofert mieszkań.`
                        ])(variables.regionPropertiesCountForSale)} Apartamenty w ${variables.regionName} mają powierzchnie ${
                            variables.propertiesFlatsRangesAreaMin !== variables.propertiesFlatsRangesAreaMax
                                ? pluralize([
                                      `${areaFormat(variables.propertiesFlatsRangesAreaMin)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`
                                  ])(variables.regionPropertiesCountForSale)
                                : `od ${areaFormat(variables.propertiesFlatsRangesAreaMin)}`
                        }.`
                    }
                ]
            },
            {
                headline: `Ile kosztuje nowe mieszkanie w Hiszpanii w ${variables.regionName}?`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Najtańsze mieszkanie w ${variables.regionName} w sprzedaży kosztuje ${priceFormat(variables.flatPriceMin, {unit: "euro"})}.`,
                            variables.averagePriceM2 > 0
                                ? `Średnia cena za m2 mieszkania w ${variables.regionName} wynosi ${priceFormat(variables.averagePriceM2, {unit: "euro"})}.`
                                : ""
                        ]
                    },
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Jesteś zainteresowany zakupem domu w Hiszpanii? Poznaj `,
                            variables.housesPropertiesCountForSale > 0
                                ? {
                                      type: TextType.LINK,
                                      href: rpAppLink.offer.listFriendlyAbroad({
                                          friendlySlug: `nowe-domy`,
                                          country: `hiszpania`,
                                          abroadRegionSlug: `${variables.regionSlug}`
                                      }),
                                      text: `ofertę domów w ${variables.regionName}.`
                                  }
                                : `ofertę domów ${variables.regionName}.`
                        ]
                    }
                ]
            }
        ];
    }
    return [
        {
            headline: `Nowe mieszkania na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.flatsOffersCountForSale,
                    propertiesCountForSale: variables.flatsPropertiesCountForSale,
                    offerTypeName: "nowych mieszkań"
                }),
                ...getFlatsRoomRangeSectionTexts(props)
            ]
        },
        {
            headline: `Ile kosztuje nowe mieszkanie ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Najtańsze nowe mieszkanie ${variables.regionNameDeclensionWhere} dostępne w sprzedaży kosztuje ${priceFormat(
                            variables.flatPriceMin
                        )}.`,
                        variables.averagePriceM2 > 0
                            ? ` Aktualnie średnia cena za m2 mieszkania ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2)}.`
                            : ""
                    ]
                    // TODO: waiting to next stage
                    // ${getEstimatedPriceText(props.selectedRegionEstimatedPrices)}
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązuje ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na mieszkania.`
                      }
            ]
        },
        {
            headline: `${upperFirst(variables.regionName)} - mieszkania gotowe do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountReady: variables.flatsCountReady,
                    offersCountReadyInThreeMonths: variables.flatsCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.flatsCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.flatsCountReadyInOneYear,
                    offerTypeNamePluralize: ["lokal mieszkalny", "lokale mieszkalne", "lokali mieszkalnych"],
                    offerTypeNameWithDeclensionOfReadyPluralize: [
                        "lokal mieszkalny zostanie oddany",
                        "lokale mieszkalne zostaną oddane",
                        "lokali mieszkalnych zostanie oddanych"
                    ],
                    offersLatestConstructionDate: variables.flatsLatestConstructionDate
                })
            ]
        },
        {
            headline: `Oferty dostępne na portalu`,
            paragraphs: [
                ...(variables.flatsGroundFloor > 0 ||
                variables.flatsGroundFloorWithGarden > 0 ||
                variables.flatsLowFloor > 0 ||
                variables.flatsHighFloor > 0 ||
                variables.flatsLastFloor > 0 ||
                variables.flatsLastFloorWithTerrace > 0
                    ? ([
                          {
                              type: ParagraphType.TEXT,
                              text: `${concatWithBrandName("Na portalu")} dostępne są oferty nowych mieszkań ${
                                  variables.regionNameDeclensionWhere
                              }, dostosowane do potrzeb odbiorców, w tym:`
                          },
                          {
                              type: ParagraphType.LIST,
                              text: [
                                  `mieszkania na parterze - ${getGeneralPropertyCountText(variables.flatsGroundFloor)}`,
                                  `mieszkania na parterze z ogródkiem - ${getGeneralPropertyCountText(variables.flatsGroundFloorWithGarden)}`,
                                  `mieszkania na niskich piętrach - ${getGeneralPropertyCountText(variables.flatsLowFloor)}`,
                                  `mieszkania na wysokich piętrach - ${getGeneralPropertyCountText(variables.flatsHighFloor)}`,
                                  `mieszkania na ostatnich piętrach - ${getGeneralPropertyCountText(variables.flatsLastFloor)}`,
                                  `mieszkania na ostatnich piętrach z tarasem - ${getGeneralPropertyCountText(variables.flatsLastFloorWithTerrace)}`
                              ]
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : []),
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Chcesz mieszkać w domu z własnym ogródkiem? Poznaj `,
                        variables.housesPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${variables.regionSlug}`}),
                                  text: `ofertę nowych domów ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych domów ${variables.regionNameDeclensionWhere}`,
                        ` i znajdź nieruchomość dopasowaną do swoich potrzeb.`
                    ]
                }
            ]
        }
        // TODO: waiting to next stage with POI information
        // {
        //     headline: `${upperFirst(variables.regionName)} - infrastruktura miejska`,
        //     paragraphs: [
        //         {
        //             type: ParagraphType.TEXT,
        //             text: `Nowe mieszkania ${variables.regionName} - szukając odpowiedniego mieszkania warto zwrócić uwagę na okolicę i poziom rozbudowania infrastruktury miejskiej. Liczba placówek edukacyjnych, zdrowotnych i rekreacyjnych ma kluczowe znaczenie dla osób zainteresowanych zakupem nowego mieszkania. ${upperFirst(variables.regionNameDeclensionWhere)} znajdziesz:`
        //         },
        //         ...getLocalInfrastructureSectionTexts({poiCount: {}})
        //     ]
        // }
    ];
};
import {upperFirst} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {concatWithBrandName} from "../../../../app/utils/concat_with_brand_name";
import {Country} from "../../../../region/types/Country";
import {getFlatsRoomRangeSectionTexts} from "./common_texts/get_flats_room_range_section_texts";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const flatOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        flatsCountReady: props.region.stats?.properties_flats_count_ready || 0,
        flatsCountReadyInThreeMonths: props.region.stats?.properties_flats_count_ready_in_3m || 0,
        flatsCountReadyInSixMonths: props.region.stats?.properties_flats_count_ready_in_6m || 0,
        flatsCountReadyInOneYear: props.region.stats?.properties_flats_count_ready_in_12m || 0,
        flatsGroundFloor: props.region.stats?.properties_flats_ground_floor || 0,
        flatsGroundFloorWithGarden: props.region.stats?.properties_flats_ground_floor_with_garden || 0,
        flatsLowFloor: props.region.stats?.properties_flats_low_floor || 0,
        flatsHighFloor: props.region.stats?.properties_flats_high_floor || 0,
        flatsLastFloor: props.region.stats?.properties_flats_last_floor || 0,
        flatsLastFloorWithTerrace: props.region.stats?.properties_flats_last_floor_with_terrace || 0,
        flatsLatestConstructionDate: props.region.stats?.properties_flats_latest_construction_date || null,
        flatsOffersCountForSale: props.region.stats?.offers_flats_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0
    };
    const getGeneralPropertyCountText = (propertiesCount: number) => {
        return `${propertiesCount} ${pluralize(["lokal", "lokale", "lokali"])(propertiesCount)}`;
    };

    if (!variables.flatsOffersCountForSale || !variables.flatsPropertiesCountForSale) {
        return [];
    }

    if (props.region.country === Country.SPAIN) {
        return [
            {
                headline: `${variables.regionName} - mieszkanie w Hiszpanii`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: `W ${upperFirst(variables.regionName)} ${pluralize([
                            `dostępna jest ${numberFormat(variables.regionPropertiesCountForSale)} oferta mieszkania.`,
                            `dostępne są ${numberFormat(variables.regionPropertiesCountForSale)} oferty mieszkań.`,
                            `dostępnych jest ${numberFormat(variables.regionPropertiesCountForSale)} ofert mieszkań.`
                        ])(variables.regionPropertiesCountForSale)} Apartamenty w ${variables.regionName} mają powierzchnie ${
                            variables.propertiesFlatsRangesAreaMin !== variables.propertiesFlatsRangesAreaMax
                                ? pluralize([
                                      `${areaFormat(variables.propertiesFlatsRangesAreaMin)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`
                                  ])(variables.regionPropertiesCountForSale)
                                : `od ${areaFormat(variables.propertiesFlatsRangesAreaMin)}`
                        }.`
                    }
                ]
            },
            {
                headline: `Ile kosztuje nowe mieszkanie w Hiszpanii w ${variables.regionName}?`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Najtańsze mieszkanie w ${variables.regionName} w sprzedaży kosztuje ${priceFormat(variables.flatPriceMin, {unit: "euro"})}.`,
                            variables.averagePriceM2 > 0
                                ? `Średnia cena za m2 mieszkania w ${variables.regionName} wynosi ${priceFormat(variables.averagePriceM2, {unit: "euro"})}.`
                                : ""
                        ]
                    },
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Jesteś zainteresowany zakupem domu w Hiszpanii? Poznaj `,
                            variables.housesPropertiesCountForSale > 0
                                ? {
                                      type: TextType.LINK,
                                      href: rpAppLink.offer.listFriendlyAbroad({
                                          friendlySlug: `nowe-domy`,
                                          country: `hiszpania`,
                                          abroadRegionSlug: `${variables.regionSlug}`
                                      }),
                                      text: `ofertę domów w ${variables.regionName}.`
                                  }
                                : `ofertę domów ${variables.regionName}.`
                        ]
                    }
                ]
            }
        ];
    }
    return [
        {
            headline: `Nowe mieszkania na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.flatsOffersCountForSale,
                    propertiesCountForSale: variables.flatsPropertiesCountForSale,
                    offerTypeName: "nowych mieszkań"
                }),
                ...getFlatsRoomRangeSectionTexts(props)
            ]
        },
        {
            headline: `Ile kosztuje nowe mieszkanie ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Najtańsze nowe mieszkanie ${variables.regionNameDeclensionWhere} dostępne w sprzedaży kosztuje ${priceFormat(
                            variables.flatPriceMin
                        )}.`,
                        variables.averagePriceM2 > 0
                            ? ` Aktualnie średnia cena za m2 mieszkania ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2)}.`
                            : ""
                    ]
                    // TODO: waiting to next stage
                    // ${getEstimatedPriceText(props.selectedRegionEstimatedPrices)}
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązuje ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na mieszkania.`
                      }
            ]
        },
        {
            headline: `${upperFirst(variables.regionName)} - mieszkania gotowe do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountReady: variables.flatsCountReady,
                    offersCountReadyInThreeMonths: variables.flatsCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.flatsCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.flatsCountReadyInOneYear,
                    offerTypeNamePluralize: ["lokal mieszkalny", "lokale mieszkalne", "lokali mieszkalnych"],
                    offerTypeNameWithDeclensionOfReadyPluralize: [
                        "lokal mieszkalny zostanie oddany",
                        "lokale mieszkalne zostaną oddane",
                        "lokali mieszkalnych zostanie oddanych"
                    ],
                    offersLatestConstructionDate: variables.flatsLatestConstructionDate
                })
            ]
        },
        {
            headline: `Oferty dostępne na portalu`,
            paragraphs: [
                ...(variables.flatsGroundFloor > 0 ||
                variables.flatsGroundFloorWithGarden > 0 ||
                variables.flatsLowFloor > 0 ||
                variables.flatsHighFloor > 0 ||
                variables.flatsLastFloor > 0 ||
                variables.flatsLastFloorWithTerrace > 0
                    ? ([
                          {
                              type: ParagraphType.TEXT,
                              text: `${concatWithBrandName("Na portalu")} dostępne są oferty nowych mieszkań ${
                                  variables.regionNameDeclensionWhere
                              }, dostosowane do potrzeb odbiorców, w tym:`
                          },
                          {
                              type: ParagraphType.LIST,
                              text: [
                                  `mieszkania na parterze - ${getGeneralPropertyCountText(variables.flatsGroundFloor)}`,
                                  `mieszkania na parterze z ogródkiem - ${getGeneralPropertyCountText(variables.flatsGroundFloorWithGarden)}`,
                                  `mieszkania na niskich piętrach - ${getGeneralPropertyCountText(variables.flatsLowFloor)}`,
                                  `mieszkania na wysokich piętrach - ${getGeneralPropertyCountText(variables.flatsHighFloor)}`,
                                  `mieszkania na ostatnich piętrach - ${getGeneralPropertyCountText(variables.flatsLastFloor)}`,
                                  `mieszkania na ostatnich piętrach z tarasem - ${getGeneralPropertyCountText(variables.flatsLastFloorWithTerrace)}`
                              ]
                          }
                      ] as ILocationGenericSection["paragraphs"])
                    : []),
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Chcesz mieszkać w domu z własnym ogródkiem? Poznaj `,
                        variables.housesPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href: rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${variables.regionSlug}`}),
                                  text: `ofertę nowych domów ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych domów ${variables.regionNameDeclensionWhere}`,
                        ` i znajdź nieruchomość dopasowaną do swoich potrzeb.`
                    ]
                }
            ]
        }
        // TODO: waiting to next stage with POI information
        // {
        //     headline: `${upperFirst(variables.regionName)} - infrastruktura miejska`,
        //     paragraphs: [
        //         {
        //             type: ParagraphType.TEXT,
        //             text: `Nowe mieszkania ${variables.regionName} - szukając odpowiedniego mieszkania warto zwrócić uwagę na okolicę i poziom rozbudowania infrastruktury miejskiej. Liczba placówek edukacyjnych, zdrowotnych i rekreacyjnych ma kluczowe znaczenie dla osób zainteresowanych zakupem nowego mieszkania. ${upperFirst(variables.regionNameDeclensionWhere)} znajdziesz:`
        //         },
        //         ...getLocalInfrastructureSectionTexts({poiCount: {}})
        //     ]
        // }
    ];
};
